import { shallowRef, watch, type FormHTMLAttributes } from "vue"
import {
	defineComponent,
	inferProps,
	renderSlot,
	useCustomFormValidation,
	ValidationHandler,
	type ReactiveComponent,
	type ValidationResult,
} from "vue-utils"
import FormMissingInfo from "./FormMissingInfo"

interface Props {
	allowNativeSubmit?: boolean
	onSubmit?: (e: Event) => void

	updateRef?(form: HTMLFormElement | undefined): void
	onSetupValidation?: (handler: ValidationHandler) => void

	validate?: () => ValidationResult | Promise<ValidationResult>
}

const Form: ReactiveComponent<Props, FormHTMLAttributes> = (props, { attrs, slots }) => {
	const formRef = shallowRef<HTMLFormElement>()
	const errorMessages = shallowRef<string[] | null>(null)

	const validation = useCustomFormValidation({
		form: formRef,
	})
	props.onSetupValidation?.(validation)

	validation.addCustomValidation(async () => {
		if (props.validate) {
			return await props.validate()
		}
		return true
	})

	watch(formRef, (formEl) => props.updateRef?.(formEl))

	let allowSubmit = false
	function onSubmit(e: SubmitEvent) {
		const form = formRef.value
		if (!form) {
			return
		}
		if (allowSubmit || attrs.novalidate) {
			if (!props.allowNativeSubmit) {
				e.preventDefault()
			}
			props.onSubmit?.(e)
			return
		}

		e.preventDefault()
		void handleSubmitAsync(e, form)
	}

	async function handleSubmitAsync(e: SubmitEvent, form: HTMLFormElement) {
		const validationResult = await validation.validateForm()
		if (!validationResult.valid) {
			if (validationResult.errorMessages?.length) {
				errorMessages.value = validationResult.errorMessages
			}
			return
		}

		window.setTimeout(() => {
			try {
				allowSubmit = true
				if (e.submitter) {
					form.requestSubmit(e.submitter)
				} else {
					form.submit()
				}
			} finally {
				allowSubmit = false
			}
		})
	}

	return () => (
		<form ref={formRef} {...attrs} onSubmit={(e) => onSubmit(e as SubmitEvent)}>
			{renderSlot(slots)}
			{errorMessages.value !== null && (
				<FormMissingInfo confirm={() => (errorMessages.value = null)} messages={errorMessages.value} />
			)}
		</form>
	)
}

export default defineComponent(Form, inferProps<Props>())
